import "./policy-dialog-style.scss";

//—— 條款 dialog ——
//
//  疊在購買 sheet 之上的純告知視窗，站內所有法律條文都從這裡出來：
//  隱私權政策、使用條款，以及行銷同意那一欄的說明。
//
//  條文直接寫在這支檔案裡，沒有另外拆內容層 —— 目前只有這個 dialog
//  會顯示它們，抽出去只是多一層轉手。哪天真的要做 /privacy 頁，
//  再把下面的 DOCS 搬出去即可，屆時它才真的有兩個消費者。
//
//  沒有取消鈕：它不改變任何狀態，關掉跟按確認是同一件事，
//  給兩顆按鈕反而會讓人以為「取消」會把前面勾的同意取消掉。
//
//  標題與按鈕在捲動區之外，讀到一半按鈕不會跑掉 ——
//  長文件最重要的就是這件事。
//
//  props:
//    open     是否顯示
//    doc      要顯示哪一份：privacy / terms / marketing
//    onClose  關閉（✕、遮罩、按鈕都走這個）

//  公司名、網址、信箱在三份條文裡都會出現。抽成常數是因為它們一旦變動
//  （換網域、換客服信箱、公司更名）必須全部一起改 ——
//  分開寫死的話一定只改到一處，而這種不一致出現在法律文件上會被拿來質疑。
const COMPANY = "萊諾創新科技有限公司";
const BRAND = "閻異閣";
const SITE = "https://revelation.leinotek.com";
//  跟頁尾、通知信、官方 LINE 是同一個窗口（constant/contact 的 SUPPORT_EMAIL）。
//  不 import 那支常數是因為這份是法律文件 —— 它的文字要能獨立於
//  前端的設定被引用與保存，而不是「看當下的設定檔是什麼」。
//  換信箱時兩邊一起改（這一行與 constant/contact.js）。
const CONTACT = "yanyige95@gmail.com";

//  兩份長文件各自有各自的修訂時間，不共用一個日期 ——
//  共用會讓沒改過的那份看起來也動過。
const PRIVACY_UPDATED = "2026 年 11 月 03 日";
const TERMS_UPDATED = "2026 年 09 月 30 日";

const mail = <a href={`mailto:${CONTACT}`}>{CONTACT}</a>;

//—— 隱私權政策 ——
//
//  依中華民國《個人資料保護法》撰寫，並對齊本站實際的技術現況 ——
//  只列真的有在用的第三方。App 內購（utils/iap-util.js）目前還是骨架、
//  尚未串接，所以沒有寫進第三方分享；串上去之後要回來補 Apple / Google Play。
//
//  ⚠️ 第五條的第三方清單與第七條的 Cookie 說明，**必須跟實際送出去的東西一致**。
//  2026-11-03 補上 Meta：裝了 Pixel 之後第七條原本那句「本服務目前不使用
//  第三方廣告追蹤 Cookie」就變成假的（_fbp 就是），而第五條結尾又寫著
//  「除上述情形外不會提供給任何第三方」—— 清單少一個對象，那句話等於不實陳述。
//  以後任何新增的追蹤或外部服務，都要回來同時改這兩條。
//  （實際送出什麼看 utils/meta-capi.js 的 user_data 與 custom_data。）
const Privacy = () => (
  <>
    <p className="policy__updated">最後更新日期：{PRIVACY_UPDATED}</p>

    <p>
      {COMPANY}（下稱「本公司」）經營之「{BRAND}」網站（
      <a href={SITE}>{SITE}</a>
      ，下稱「本服務」）非常重視您的個人資料保護。本政策說明我們如何蒐集、處理、利用您的個人資料，並依中華民國《個人資料保護法》及相關法令辦理。使用本服務即表示您已閱讀並同意本政策全部內容。
    </p>

    <h4>一、適用範圍</h4>
    <p>
      本政策僅適用於本服務所蒐集之個人資料。當您點擊本網站中之外部連結（如綠界科技、Google
      等），請另行參閱該第三方服務之隱私權政策。
    </p>

    <h4>二、我們蒐集哪些個人資料</h4>
    <p>為提供命理分析與相關服務，本服務可能蒐集以下類別之個人資料：</p>
    <ul>
      <li>
        <strong>帳戶識別資料：</strong>
        透過 Google 第三方帳號登入所取得之電子郵件、姓名、頭像。
      </li>
      <li>
        <strong>命理分析所需資料：</strong>
        暱稱、出生年月日、出生時辰、出生地經緯度、性別、感情狀況、工作狀態、您所填寫之關注問題等。
      </li>
      <li>
        <strong>交易與發票資料：</strong>
        訂單編號、金額、付款方式（信用卡完整卡號由綠界處理，我方不留存）、電子發票資訊（載具號碼、統一編號、抬頭、買受人姓名、通知
        Email、愛心碼等）。
      </li>
      <li>
        <strong>使用行為與內容：</strong>
        您產生的 AI 命理報告、提問與回覆紀錄、票券使用紀錄。
      </li>
      <li>
        <strong>推播與裝置資料：</strong>
        裝置推播識別碼、作業系統類型（用於報告完成與服務結果通知）。
      </li>
      <li>
        <strong>技術資料：</strong>
        裝置類型、瀏覽器資訊、Cookie、伺服器 log（供資安、除錯與服務穩定用）。
      </li>
    </ul>

    <h4>三、蒐集目的與利用方式</h4>
    <ul>
      <li>提供命理分析、AI 報告產出、提問諮詢等本服務之核心功能。</li>
      <li>處理訂單、開立電子發票、退款與客戶服務。</li>
      <li>寄送與服務相關之交易通知（如發票、購買確認、報告完成通知）。</li>
      <li>統計分析、產品改善與服務品質提升（僅使用去識別化資料）。</li>
      <li>依法律或主管機關要求提供必要資訊。</li>
    </ul>
    <p>
      本服務
      <strong>不會將您的個人資料作為行銷用途</strong>
      ，除非您於結帳頁或其他頁面另行勾選同意；您可隨時來信取消，不影響既有服務之使用。
    </p>

    <h4>四、保存期間</h4>
    <p>
      您的個人資料原則上保存至您註銷帳戶止；發票、交易與稅務相關紀錄依《稅捐稽徵法》規定保存
      5 年；伺服器 log 保存 30 日。您可隨時要求刪除。
    </p>

    <h4>五、資料提供與第三方分享</h4>
    <p>本服務為完成上述目的，會將必要資料提供給下列合作對象：</p>
    <ul>
      <li>
        <strong>綠界科技（ECPay）：</strong>
        金流付款處理、電子發票代開立。
      </li>
      <li>
        <strong>Google Cloud / Firebase：</strong>
        帳號驗證、檔案儲存、推播通知、應用程式運算。
      </li>
      <li>
        <strong>Google Gemini：</strong>
        AI 命理報告產出；僅傳送必要之命盤、生日等資訊，並要求供應商依合約不得留存訓練。
      </li>
      <li>
        <strong>Meta Platforms：</strong>
        廣告成效衡量與再行銷。僅傳送經單向雜湊處理之電子郵件、瀏覽器識別碼、
        瀏覽之頁面與購買金額；
        <strong>不傳送您的命盤內容、問診回覆或報告內文</strong>。
      </li>
      <li>
        <strong>其他：</strong>
        因法律要求或司法機關依法函詢時。
      </li>
    </ul>
    <p>
      除上述情形外，本服務
      <strong>不會將您的個人資料販售、出租或提供給任何第三方</strong>。
    </p>

    <h4>六、您的權利</h4>
    <p>依個人資料保護法第 3 條，您可隨時就本服務保有之個人資料主張下列權利：</p>
    <ul>
      <li>查詢或請求閱覽</li>
      <li>請求製給複製本</li>
      <li>請求補充或更正</li>
      <li>請求停止蒐集、處理或利用</li>
      <li>請求刪除</li>
    </ul>
    <p>請寄信至 {mail} 主張，我方將於 7 個工作天內回覆處理。</p>

    <h4>七、Cookie 與追蹤技術</h4>
    <p>
      本服務使用 httpOnly Cookie
      儲存您的登入狀態，屬服務運行所必要，關閉將導致無法使用本服務。
    </p>
    <p>
      另為衡量廣告成效，本服務使用 Meta 像素（Meta Pixel），於您的瀏覽器儲存
      「_fbp」、「_fbc」
      等識別用 Cookie，並將您瀏覽之頁面、購買紀錄及經單向雜湊處理之電子郵件傳送予 Meta
      Platforms（伺服器位於境外，屬個人資料之國際傳輸）。您可透過瀏覽器設定封鎖此類
      Cookie，或於 Meta 帳號之廣告偏好設定中調整；拒絕不影響本服務核心功能之使用。
    </p>

    <h4>八、未成年人保護</h4>
    <p>
      本服務適用對象為年滿 18 歲之個人。未滿 18
      歲者，請於法定代理人陪同並取得同意後始得使用本服務及提供個人資料。
    </p>

    <h4>九、資料安全</h4>
    <p>
      本服務採用 HTTPS 加密傳輸；資料儲存於 MongoDB Atlas 及 Firebase Storage
      並依環境隔離；發票與敏感 credentials 存放於 Google Cloud Secret
      Manager。我方仍無法保證絕對安全，敬請您妥善保管帳號並避免於公用電腦登入。
    </p>

    <h4>十、政策修訂</h4>
    <p>
      本政策可能因法規或服務變動而修訂，修訂後將於本頁公告並更新「最後更新日期」；重大變更將透過
      Email 或站內公告通知。
    </p>

    <h4>十一、聯絡我們</h4>
    <p>本政策相關問題，或欲行使個資權利者，請寄信至：{mail}</p>
  </>
);

//—— 使用條款 ——
//
//  服務項目照 constant/products.js 的五支產品與問卦／提問功能列，
//  沒有的東西不寫（例如點數儲值：本站用的是票券，不是點數）。
//  問卦與提問由 AI 角色回覆（伏爻子見 pages/fuyaozi-ask、白夜見 pages/baiye-question），
//  不是真人命理師，所以免責寫的是 AI 產出而非「老師之建議」。
const Terms = () => (
  <>
    <p className="policy__updated">最後更新日期：{TERMS_UPDATED}</p>

    <p>
      歡迎使用「{BRAND}」（下稱「本服務」）。本使用條款（下稱「本條款」）為您（下稱「使用者」）與
      {COMPANY}
      （下稱「本公司」）間之契約。使用本服務即視為您已閱讀並同意本條款全部內容。若不同意，請立即停止使用。
    </p>

    <h4>一、服務內容</h4>
    <p>本服務提供之項目包含但不限於：</p>
    <ul>
      <li>白夜・八字批命、鏡月・紫微斗數、判官・流年之書等 AI 命理報告</li>
      <li>緋紅・愛情合盤等雙人合盤分析</li>
      <li>伏爻子・文王聖卦之起卦與解卦</li>
      <li>問卦與提問（由本服務之 AI 角色回覆）</li>
      <li>問卦券、問事券之購買與使用</li>
    </ul>

    <h4>二、帳號註冊與使用資格</h4>
    <ul>
      <li>使用者須透過 Google 帳號登入並完成註冊資料填寫。</li>
      <li>使用者應確保所提供之資料真實、正確且完整。</li>
      <li>
        使用者須年滿 18 歲；未滿 18 歲者，須經法定代理人同意始得使用本服務。
      </li>
      <li>
        帳號不得轉讓、出借或販售；因帳號被盜用致生之損害，除可歸責本公司者外，由使用者自行負責。
      </li>
    </ul>

    <h4>三、命理服務性質聲明（免責）</h4>
    <p>
      本服務所提供之命理分析、AI 報告及問答內容，屬於
      <strong>傳統東方文化參考</strong>，
      <strong>不構成醫療、法律、投資、婚姻或其他重大決策之專業建議</strong>
      。使用者不得依本服務內容作為前述決策之唯一或主要依據；因參考本服務內容所生之任何損害，本公司概不負責。
    </p>

    <h4>四、訂購、付款與電子發票</h4>
    <ul>
      <li>
        本服務所有訂購由綠界科技（ECPay）處理付款，本公司不留存信用卡完整資訊。
      </li>
      <li>
        付款完成後，本服務將依中華民國《統一發票使用辦法》開立電子發票，並依您選擇之類型（會員電子發票／手機條碼載具／捐贈／統一編號）處理。
      </li>
      <li>發票通知將寄至您於結帳頁提供之 Email。</li>
    </ul>

    <h4>五、退款政策</h4>
    <p>
      依《消費者保護法》第 19 條第 2 項及《通訊交易解除權合理例外情事適用準則》第
      2 條第 5 款，
      <strong>
        「非以有形媒介提供之數位內容或一經提供即為完成之線上服務」不適用七日鑑賞期
      </strong>
      。因此：
    </p>
    <ul>
      <li>
        <strong>AI 命理報告、卦象解讀、問卦與提問回覆：</strong>
        一經產出或提供即完成服務，恕不接受退款。
      </li>
      <li>
        <strong>問卦券、問事券：</strong>
        購買成功後不得轉讓、不得退換現金；因系統錯誤或重複扣款所生之爭議，請於 7
        日內來信申訴，經核實後將全額退款。
      </li>
      <li>
        <strong>電子發票作廢：</strong>
        若您因退款需求辦理發票作廢，本公司將於當月內作廢；跨月則以折讓單處理。
      </li>
    </ul>

    <h4>六、智慧財產權</h4>
    <p>
      本服務所產出之 AI
      報告內容，其著作權由本公司保留，僅授權使用者個人非商業使用；未經書面同意，不得複製、散布、公開傳輸、改作或商業利用。
    </p>

    <h4>七、禁止行為</h4>
    <p>使用本服務時，您不得：</p>
    <ul>
      <li>冒用他人身份、提供不實資料或干擾其他使用者權益。</li>
      <li>
        對本服務或其伺服器進行未授權之爬蟲、掃描、破解、逆向工程或壓力測試。
      </li>
      <li>將本服務用於違法目的、詐騙、賭博、或違反公序良俗之用途。</li>
      <li>未經授權將本服務轉售、代購、或作為商業媒介。</li>
    </ul>
    <p>違反上述規定者，本公司得逕行暫停或終止您的帳號並保留法律追訴權利。</p>

    <h4>八、服務中斷與變更</h4>
    <p>
      本公司得為系統維護、升級、修繕、法規變更或不可抗力事由，暫時中斷或永久終止全部或部分服務。除故意或重大過失外，本公司不對前述情事所致之損害負責。
    </p>

    <h4>九、免責條款</h4>
    <ul>
      <li>命理分析結果為文化參考性質，不保證任何預測結果之實現。</li>
      <li>
        AI 產出之報告可能存在資料誤差或詮釋差異，本公司力求準確但不擔保絕對正確。
      </li>
      <li>本公司對於因網路壅塞、第三方服務中斷所致之損害不負賠償責任。</li>
    </ul>

    <h4>十、條款修訂</h4>
    <p>
      本條款可能因法規或服務變動而修訂，修訂後將於本頁公告。使用者於修訂後繼續使用本服務，視為同意修訂內容。重大變更將另行
      Email 或站內通知。
    </p>

    <h4>十一、準據法與管轄</h4>
    <p>
      本條款之解釋及適用，以及使用者與本公司間因本服務產生之爭議，均以中華民國法律為準據法，並合意以
      <strong>臺灣臺北地方法院</strong>為第一審管轄法院。
    </p>

    <h4>十二、聯絡我們</h4>
    <p>對本條款有任何疑問，請寄信至：{mail}</p>
  </>
);

//—— 行銷訊息接收同意 ——
//
//  結帳頁「同意接收行銷資訊（選填）」那顆勾選框底下的「詳細資訊」展開的全文。
const Marketing = () => (
  <p>
    本人同意{BRAND}
    透過所提供之電子郵件與手機號碼，接收各項優惠、活動參與及新服務相關之行銷訊息。
  </p>
);

const DOCS = {
  privacy: { title: `${BRAND} 個人資料保護政策`, Body: Privacy },
  terms: { title: `${BRAND} 使用條款`, Body: Terms },
  marketing: { title: "行銷訊息接收同意", Body: Marketing },
};

const PolicyDialog = ({ open, doc, onClose }) => {
  const picked = DOCS[doc];
  if (!open || !picked) return null;

  const { title, Body } = picked;

  return (
    <div className="policy" role="dialog" aria-modal="true" aria-label={title}>
      {/* 遮罩可點關閉 */}
      <div className="policy__mask" onClick={onClose} />

      <div className="policy__panel">
        <button
          type="button"
          className="policy__close"
          onClick={onClose}
          aria-label="關閉"
        >
          ✕
        </button>

        <h3 className="policy__title">{title}</h3>

        <div className="policy__scroll">
          <Body />
        </div>

        <button type="button" className="policy__ok" onClick={onClose}>
          確認
        </button>
      </div>
    </div>
  );
};

export default PolicyDialog;
